import { useState, useMemo, useEffect } from 'react';
import { downloadCsv } from '@/utils/exportCsv';
import { useRBAC } from '@/hooks/useRBAC';
import NoPermission from '@/components/base/NoPermission';
import { httpGet, httpPatch } from '@/services/http';

interface Registration {
  id: number;
  name: string;
  email: string;
  phone: string;
  company: string;
  event_id: number;
  event_name: string;
  registered_at: string;
  status: 'pending' | 'approved' | 'rejected';
  notes: string;
  created_at: string;
  updated_at: string;
}

const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-600',
};
const STATUS_LABEL: Record<string, string> = { pending: '待审核', approved: '已通过', rejected: '已拒绝' };

// 格式化日期，支持多种格式
function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  
  // 如果是纯日期格式 (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-');
    return `${year}-${month}-${day}`;
  }
  
  // 如果是 ISO 格式，添加时间部分
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Asia/Shanghai'
    });
  } catch {
    return dateStr;
  }
}

function ExportDropdown({ onExportAll, onExportFiltered, filteredCount }: {
  onExportAll: () => void;
  onExportFiltered: () => void;
  filteredCount: number;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-lg text-xs font-medium text-[#374151] hover:bg-[#F9FAFB] cursor-pointer whitespace-nowrap transition-colors"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-download-2-line text-sm" />
        </div>
        导出 CSV
        <div className="w-3 h-3 flex items-center justify-center">
          {open ? <i className="ri-arrow-up-s-line text-xs" /> : <i className="ri-arrow-down-s-line text-xs" />}
        </div>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-[#E5E7EB] rounded-lg shadow-sm z-20 overflow-hidden">
          <button
            onClick={() => { onExportAll(); setOpen(false); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-[#374151] hover:bg-[#F9FAFB] cursor-pointer transition-colors text-left"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-file-list-3-line text-[#6B7280]" />
            </div>
            导出全部数据
          </button>
          <button
            onClick={() => { onExportFiltered(); setOpen(false); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-[#374151] hover:bg-[#F9FAFB] cursor-pointer transition-colors text-left border-t border-[#F3F4F6]"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-filter-3-line text-[#6B7280]" />
            </div>
            导出筛选结果（{filteredCount}）
          </button>
        </div>
      )}
    </div>
  );
}

export default function RegistrationsPage() {
  const { can } = useRBAC();
  const canView = can('registrations:view');
  const canApprove = can('registrations:approve');
  const canDelete = can('registrations:delete');

  const [regs, setRegs] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterEvent, setFilterEvent] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Registration | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [toast, setToast] = useState('');

  // 加载报名数据
  useEffect(() => {
    loadRegistrations();
  }, []);

  const loadRegistrations = async () => {
    setLoading(true);
    try {
      const data = await httpGet<Registration[]>('/admin/registrations');
      setRegs(data);
    } catch (error) {
      console.error('加载报名数据失败:', error);
      showToast('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const eventNames = useMemo(() => {
    const set = new Set(regs.map((r) => r.event_name));
    return Array.from(set);
  }, [regs]);

  const filtered = useMemo(() => {
    return regs.filter((r) => {
      if (filterStatus !== 'all' && r.status !== filterStatus) return false;
      if (filterEvent !== 'all' && r.event_name !== filterEvent) return false;
      if (search && !r.name.includes(search) && !r.email.toLowerCase().includes(search.toLowerCase()) && !r.company.includes(search)) return false;
      return true;
    }).sort((a, b) => new Date(b.registered_at).getTime() - new Date(a.registered_at).getTime());
  }, [regs, filterStatus, filterEvent, search]);

  // Batch helpers
  const allFilteredSelected = filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((r) => r.id)));
    }
  };

  const toggleSelectOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBatchStatus = async (status: Registration['status']) => {
    try {
      const ids = Array.from(selectedIds);
      if (status === 'approved') {
        await httpPatch('/admin/registrations/batch-approve', { ids });
      } else if (status === 'rejected') {
        await httpPatch('/admin/registrations/batch-reject', { ids });
      } else {
        // 批量设为待审
        await Promise.all(ids.map(id => httpPatch(`/admin/registrations/${id}`, { status })));
      }
      
      setRegs(regs.map(r => selectedIds.has(r.id) ? { ...r, status } : r));
      const labelMap: Record<string, string> = { approved: '通过', rejected: '拒绝', pending: '待审核' };
      showToast(`已将 ${selectedIds.size} 条报名设为「${labelMap[status]}」`);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('批量更新失败:', error);
      showToast('批量更新失败');
    }
  };

  const updateStatus = async (id: number, status: Registration['status']) => {
    try {
      await httpPatch(`/admin/registrations/${id}`, { status });
      const updated = regs.map((r) => r.id === id ? { ...r, status } : r);
      setRegs(updated);
      if (selected?.id === id) setSelected({ ...selected, status });
      showToast('状态更新成功');
    } catch (error) {
      console.error('更新状态失败:', error);
      showToast('更新失败');
    }
  };

  const pendingCount = regs.filter((r) => r.status === 'pending').length;

  const REG_CSV_HEADERS = [
    { key: 'name', label: '姓名' },
    { key: 'email', label: '邮箱' },
    { key: 'phone', label: '电话' },
    { key: 'company', label: '所属机构' },
    { key: 'event_name', label: '报名活动' },
    { key: 'registered_at', label: '报名时间' },
    { key: 'status', label: '状态' },
    { key: 'notes', label: '备注' },
  ];

  const handleExportAll = () => {
    const rows = regs.map((r) => ({ ...r, status: STATUS_LABEL[r.status] ?? r.status })) as unknown as Record<string, unknown>[];
    downloadCsv(rows, REG_CSV_HEADERS, `报名数据_全部_${new Date().toISOString().slice(0, 10)}`);
  };

  const handleExportFiltered = () => {
    const rows = filtered.map((r) => ({ ...r, status: STATUS_LABEL[r.status] ?? r.status })) as unknown as Record<string, unknown>[];
    downloadCsv(rows, REG_CSV_HEADERS, `报名数据_筛选结果_${new Date().toISOString().slice(0, 10)}`);
  };

  if (!canView) return <NoPermission resource="活动报名" />;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {toast && (
        <div className="fixed top-5 right-5 z-50 px-4 py-2.5 bg-[#18181B] text-white text-sm rounded-xl shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#18181B]">活动报名管理</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">共 {regs.length} 条报名，{pendingCount} 条待审核</p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-lg border border-amber-100">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-amber-600 text-sm" />
              </div>
              <span className="text-amber-700 text-xs font-medium">{pendingCount} 待处理</span>
            </div>
          )}
          <ExportDropdown onExportAll={handleExportAll} onExportFiltered={handleExportFiltered} filteredCount={filtered.length} />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none w-4 h-full">
            <i className="ri-search-line text-[#9CA3AF] text-sm" />
          </div>
          <input
            type="text"
            placeholder="搜索姓名/邮箱/机构..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] bg-white w-56"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 cursor-pointer"
        >
          <option value="all">全部状态</option>
          <option value="pending">待审核</option>
          <option value="approved">已通过</option>
          <option value="rejected">已拒绝</option>
        </select>
        <select
          value={filterEvent}
          onChange={(e) => setFilterEvent(e.target.value)}
          className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 cursor-pointer max-w-[200px]"
        >
          <option value="all">全部活动</option>
          {eventNames.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <span className="flex items-center text-xs text-[#9CA3AF] ml-auto">共 {filtered.length} 条结果</span>
      </div>

      {/* Batch Action Bar */}
      {someSelected && (
        <div className="flex items-center gap-3 px-4 py-2.5 mb-3 bg-[#18181B] rounded-xl">
          <span className="text-white text-xs font-medium whitespace-nowrap">已选 {selectedIds.size} 条</span>
          <div className="flex-1" />
          {canApprove && <>
            <button onClick={() => handleBatchStatus('approved')} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors">
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-checkbox-circle-line" /></div>
              批量通过
            </button>
            <button onClick={() => handleBatchStatus('rejected')} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors">
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-close-circle-line" /></div>
              批量拒绝
            </button>
            <button onClick={() => handleBatchStatus('pending')} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#374151] hover:bg-[#4B5563] text-white text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors">
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-refresh-line" /></div>
              撤回为待审
            </button>
          </>}
          <button onClick={() => setSelectedIds(new Set())} className="w-6 h-6 flex items-center justify-center text-white/50 hover:text-white cursor-pointer transition-colors">
            <i className="ri-close-line text-sm" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#2C7DA0] border-t-transparent"></div>
            </div>
          ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#F3F4F6] bg-[#FAFAFA]">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    className="w-3.5 h-3.5 rounded border-[#D1D5DB] cursor-pointer accent-[#18181B]"
                  />
                </th>
                {['姓名', '邮箱', '机构', '活动', '报名时间', '状态', '操作'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-medium text-[#9CA3AF] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-[#9CA3AF] text-sm">暂无数据</td>
                </tr>
              ) : filtered.map((r) => {
                const isSelected = selectedIds.has(r.id);
                return (
                  <tr key={r.id} className={`border-b border-[#F9FAFB] transition-colors ${isSelected ? 'bg-amber-50/60' : 'hover:bg-[#FAFAFA]'}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectOne(r.id)}
                        className="w-3.5 h-3.5 rounded border-[#D1D5DB] cursor-pointer accent-[#18181B]"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelected(r)} className="font-medium text-[#18181B] hover:text-[#F59E0B] cursor-pointer text-xs transition-colors text-left whitespace-nowrap">
                        {r.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-[#6B7280] text-xs">{r.email}</td>
                    <td className="px-4 py-3 text-[#6B7280] text-xs">{r.company}</td>
                    <td className="px-4 py-3 text-[#6B7280] text-xs max-w-[140px] truncate">{r.event_name}</td>
                    <td className="px-4 py-3 text-[#6B7280] text-xs whitespace-nowrap">
                      {formatDate(r.registered_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[r.status]}`}>
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {canApprove && r.status !== 'approved' && (
                          <button onClick={() => updateStatus(r.id, 'approved')} className="px-2 py-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded transition-colors cursor-pointer whitespace-nowrap">通过</button>
                        )}
                        {canApprove && r.status !== 'rejected' && (
                          <button onClick={() => updateStatus(r.id, 'rejected')} className="px-2 py-1 text-[10px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors cursor-pointer whitespace-nowrap">拒绝</button>
                        )}
                        {canApprove && r.status !== 'pending' && (
                          <button onClick={() => updateStatus(r.id, 'pending')} className="px-2 py-1 text-[10px] font-medium text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded transition-colors cursor-pointer whitespace-nowrap">撤回</button>
                        )}
                        {!canApprove && <span className="text-[10px] text-[#D1D5DB]">仅查看</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-[#18181B]">报名详情</h3>
              <button onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#6B7280] cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-3">
              {[
                ['姓名', selected.name],
                ['邮箱', selected.email],
                ['电话', selected.phone],
                ['所属机构', selected.company],
                ['报名活动', selected.event_name],
                ['报名时间', formatDate(selected.registered_at)],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-3">
                  <span className="text-xs text-[#9CA3AF] w-20 shrink-0 pt-0.5">{k}</span>
                  <span className="text-sm text-[#18181B] break-all">{v}</span>
                </div>
              ))}
              <div className="flex gap-3">
                <span className="text-xs text-[#9CA3AF] w-20 shrink-0 pt-1.5">状态</span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLOR[selected.status]}`}>
                  {STATUS_LABEL[selected.status]}
                </span>
              </div>
            </div>
            {canApprove && (
              <div className="flex gap-2 mt-6">
                {selected.status !== 'approved' && (
                  <button onClick={() => updateStatus(selected.id, 'approved')} className="flex-1 py-2.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                    通过审核
                  </button>
                )}
                {selected.status !== 'rejected' && (
                  <button onClick={() => updateStatus(selected.id, 'rejected')} className="flex-1 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                    拒绝
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
